import * as THREE from "three";
import { useViewerStore } from "../../../../../store/store";

type PoolKey = "port" | "land" | "default";

function guessPoolKeyFromTexture(tex: THREE.Texture | null): PoolKey {
    if (!tex) return "default";
    const url =
        (tex as any)?.image?.currentSrc ||
        (tex as any)?.image?.src ||
        (tex as any)?.source?.data ||
        "";
    const lc = (url || "").toString().toLowerCase();
    if (lc.includes("port")) return "port";
    if (lc.includes("land")) return "land";
    return "default";
}

function makeModuleShaderMaterial(tex: THREE.Texture | null) {
    const useMap = tex ? 1 : 0;
    if (tex) {
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.needsUpdate = true;
    }

    const uniforms = {
        uMap:     { value: tex ?? null },
        uUseMap:  { value: useMap },
        uOpacity: { value: 1.0 },
    };

    const vertexShader = `
    attribute vec3 instanceColor;
    attribute vec3 instanceEmissive;

    // per-instance UV transform
    // uvScale = (sx, sy), uvRot = rotation (radians)
    attribute vec2 instanceUVScale;
    attribute float instanceUVRot;

    varying vec2 vUv;
    varying vec3 vColor;
    varying vec3 vEmissive;
    varying vec2 vUvScale;
    varying float vUvRot;

    void main() {
      vUv = uv;
      vColor = instanceColor;
      vEmissive = instanceEmissive;
      vUvScale = instanceUVScale;
      vUvRot = instanceUVRot;

      vec4 mvPos = modelViewMatrix * (instanceMatrix * vec4(position, 1.0));
      gl_Position = projectionMatrix * mvPos;
    }
  `;

    const fragmentShader = `
    precision mediump float;
    uniform sampler2D uMap;
    uniform int   uUseMap;
    uniform float uOpacity;

    varying vec2 vUv;
    varying vec3 vColor;
    varying vec3 vEmissive;
    varying vec2 vUvScale;
    varying float vUvRot;

    vec2 rotateAroundCenter(vec2 uv, float angle) {
      // center to 0, rotate, back to 0.5. Scale applied outside.
      vec2 c = uv - vec2(0.5);
      float s = sin(angle), cA = cos(angle);
      mat2 R = mat2(cA, -s, s, cA);
      return (R * c) + vec2(0.5);
    }

    void main() {
      vec3 albedo = vColor;
      if (uUseMap == 1) {
        // Apply per-instance rotation then scale (repeat)
        vec2 uvT = rotateAroundCenter(vUv, vUvRot);
        uvT = (uvT - vec2(0.5)) * vUvScale + vec2(0.5);
        vec4 texel = texture2D(uMap, uvT);
        albedo *= texel.rgb;
      }
      vec3 outRGB = albedo + vEmissive; // unlit
      gl_FragColor = vec4(outRGB, uOpacity);
    }
  `;

    return new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms,
        transparent: false,
        depthTest: true,
        depthWrite: true,
        side: THREE.FrontSide,
    });
}


class MaterialColorProxy {
    private _color = new THREE.Color(0xffffff);
    constructor(private owner: InstancedModuleObject) {}

    setHex(hex: number) {
        this._color.setHex((hex >>> 0) & 0xffffff);
        this.owner.__setInstanceColor(this._color);
        return this;
    }
    set(v: THREE.Color | number | string) {
        this._color.set(v as any);
        this.owner.__setInstanceColor(this._color);
        return this;
    }

    getHex() { return this._color.getHex(); }
    get r() { return this._color.r; }
    get g() { return this._color.g; }
    get b() { return this._color.b; }
    get isColor() { return true; }
}

class MaterialEmissiveProxy {
    private _color = new THREE.Color(0x000000);
    constructor(private owner: InstancedModuleObject) {}

    setHex(hex: number) {
        this._color.setHex((hex >>> 0) & 0xffffff);
        this.owner.__setInstanceEmissive(this._color);
        return this;
    }
    set(v: THREE.Color | number | string) {
        this._color.set(v as any);
        this.owner.__setInstanceEmissive(this._color);
        return this;
    }

    getHex() { return this._color.getHex(); }
    get isColor() { return true; }
}

type FacadeSnapshot = {
    __instancedSnapshot: true;
    colorHex: number;
    emissiveHex: number;
    map: THREE.Texture | null;
    uvRepeat: { x: number; y: number };
    uvRotation: number;
};

class MaterialFacade {
    public readonly color: MaterialColorProxy;
    public readonly emissive: MaterialEmissiveProxy;
    private _map: THREE.Texture | null = null;

    constructor(private owner: InstancedModuleObject) {
        this.color = new MaterialColorProxy(owner);
        this.emissive = new MaterialEmissiveProxy(owner);
    }

    get needsUpdate() { return false; }
    set needsUpdate(_: boolean) {}

    get map() { return this._map; }
    set map(tex: THREE.Texture | null) {
        this._map = tex ?? null;
        this.owner.__setPoolByMap(this._map);

        if (tex) {
            const rx = (tex.repeat?.x ?? 1);
            const ry = (tex.repeat?.y ?? 1);
            const rot = (tex.rotation ?? 0);
            this.owner.__setInstanceUV(rx, ry, rot);
        } else {
            this.owner.__setInstanceUV(1, 1, 0);
        }
    }

    clone(): FacadeSnapshot {
        return {
            __instancedSnapshot: true,
            colorHex: this.color.getHex(),
            emissiveHex: this.emissive.getHex(),
            map: this._map,
            uvRepeat: { x: this.owner.__getUVScale().x, y: this.owner.__getUVScale().y },
            uvRotation: this.owner.__getUVRot(),
        };
    }
}


class ModuleInstancerPool {
    readonly step = 1024;
    readonly key: PoolKey;
    readonly geom: THREE.BoxGeometry;
    readonly mat: THREE.ShaderMaterial;
    mesh: THREE.InstancedMesh;

    private active = new Set<number>();
    private freeIndices: number[] = [];
    private highest = -1;

    constructor(key: PoolKey, texture: THREE.Texture | null, container: THREE.Object3D) {
        this.key = key;
        this.geom = new THREE.BoxGeometry(1, 1, 0.02);
        this.mat = makeModuleShaderMaterial(texture);

        this.mesh = new THREE.InstancedMesh(this.geom, this.mat, this.step);
        this.mesh.name = `ModuleInstancer:${key}`;
        this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

        const ic = new Float32Array(this.step * 3);
        for (let i = 0; i < this.step; i++) {
            const o = i * 3;
            ic[o + 0] = 1; ic[o + 1] = 1; ic[o + 2] = 1;
        }
        const instColAttr = new THREE.InstancedBufferAttribute(ic, 3);
        instColAttr.setUsage(THREE.DynamicDrawUsage);
        this.mesh.geometry.setAttribute("instanceColor", instColAttr);

        const ie = new Float32Array(this.step * 3); // zeros
        const instEmAttr = new THREE.InstancedBufferAttribute(ie, 3);
        instEmAttr.setUsage(THREE.DynamicDrawUsage);
        this.mesh.geometry.setAttribute("instanceEmissive", instEmAttr);

        const uvScale = new Float32Array(this.step * 2);
        for (let i = 0; i < this.step; i++) {
            const o = i * 2;
            uvScale[o + 0] = 1; uvScale[o + 1] = 1;
        }
        const instUVScale = new THREE.InstancedBufferAttribute(uvScale, 2);
        instUVScale.setUsage(THREE.DynamicDrawUsage);
        this.mesh.geometry.setAttribute("instanceUVScale", instUVScale);

        const uvRot = new Float32Array(this.step);
        const instUVRot = new THREE.InstancedBufferAttribute(uvRot, 1);
        instUVRot.setUsage(THREE.DynamicDrawUsage);
        this.mesh.geometry.setAttribute("instanceUVRot", instUVRot);

        this.mesh.count = 0;
        container.add(this.mesh);
    }

    private grow() {
        const old = this.mesh;
        const cap = old.instanceMatrix.count;
        const newCap = cap + this.step;

        const nm = new THREE.InstancedMesh(this.geom, this.mat, newCap);
        nm.name = old.name;
        nm.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

        // Copy matrices
        const tmp = new THREE.Matrix4();
        for (let i = 0; i < cap; i++) {
            old.getMatrixAt(i, tmp);
            nm.setMatrixAt(i, tmp);
        }

        const oldColorAttr = old.geometry.getAttribute("instanceColor") as THREE.InstancedBufferAttribute | undefined;
        const newColorArr = new Float32Array(newCap * 3);
        if (oldColorAttr) newColorArr.set(oldColorAttr.array as Float32Array, 0);
        for (let i = cap; i < newCap; i++) {
            const o = i * 3;
            newColorArr[o + 0] = 1; newColorArr[o + 1] = 1; newColorArr[o + 2] = 1;
        }
        const newColorAttr = new THREE.InstancedBufferAttribute(newColorArr, 3);
        newColorAttr.setUsage(THREE.DynamicDrawUsage);
        nm.geometry.setAttribute("instanceColor", newColorAttr);

        // Copy & extend instanceEmissive
        const oldEmAttr = old.geometry.getAttribute("instanceEmissive") as THREE.InstancedBufferAttribute | undefined;
        const newEmArr = new Float32Array(newCap * 3);
        if (oldEmAttr) newEmArr.set(oldEmAttr.array as Float32Array, 0);
        const newEmAttr = new THREE.InstancedBufferAttribute(newEmArr, 3);
        newEmAttr.setUsage(THREE.DynamicDrawUsage);
        nm.geometry.setAttribute("instanceEmissive", newEmAttr);

        const oldUVS = old.geometry.getAttribute("instanceUVScale") as THREE.InstancedBufferAttribute | undefined;
        const newUVScale = new Float32Array(newCap * 2);
        if (oldUVS) newUVScale.set(oldUVS.array as Float32Array, 0);
        for (let i = cap; i < newCap; i++) {
            const o = i * 2;
            newUVScale[o + 0] = 1; newUVScale[o + 1] = 1;
        }
        const newUVSAttr = new THREE.InstancedBufferAttribute(newUVScale, 2);
        newUVSAttr.setUsage(THREE.DynamicDrawUsage);
        nm.geometry.setAttribute("instanceUVScale", newUVSAttr);

        // Copy & extend UV rot
        const oldUVR = old.geometry.getAttribute("instanceUVRot") as THREE.InstancedBufferAttribute | undefined;
        const newUVRot = new Float32Array(newCap);
        if (oldUVR) newUVRot.set(oldUVR.array as Float32Array, 0);
        const newUVRAttr = new THREE.InstancedBufferAttribute(newUVRot, 1);
        newUVRAttr.setUsage(THREE.DynamicDrawUsage);
        nm.geometry.setAttribute("instanceUVRot", newUVRAttr);

        nm.count = old.count;

        const parent = old.parent;
        if (parent) {
            parent.remove(old);
            parent.add(nm);
        }
        old.geometry.deleteAttribute?.("instanceColor");
        old.geometry.deleteAttribute?.("instanceEmissive");
        old.geometry.deleteAttribute?.("instanceUVScale");
        old.geometry.deleteAttribute?.("instanceUVRot");
        (old as any).dispose?.();

        this.mesh = nm;
        this.mat.needsUpdate = true;
    }

    private ensure(idx: number) {
        while (idx >= this.mesh.instanceMatrix.count) this.grow();
    }

    alloc(): number {
        const idx = this.freeIndices.length > 0 ? this.freeIndices.pop()! : this.highest + 1;
        this.ensure(idx);
        this.active.add(idx);
        this.highest = Math.max(this.highest, idx);
        this.mesh.count = this.highest + 1;

        // Hide initially
        const hide = new THREE.Matrix4().makeScale(0, 0, 0);
        this.mesh.setMatrixAt(idx, hide);
        this.mesh.instanceMatrix.needsUpdate = true;

        // Reset visual state
        this.setColor(idx, new THREE.Color(0xffffff));
        this.setEmissive(idx, new THREE.Color(0x000000));
        this.setUV(idx, 1, 1, 0);

        return idx;
    }

    freeIndex(idx: number) {
        if (!this.active.has(idx)) return;
        this.active.delete(idx);
        this.freeIndices.push(idx);

        // Hide freed slot
        const hide = new THREE.Matrix4().makeScale(0, 0, 0);
        this.mesh.setMatrixAt(idx, hide);
        this.mesh.instanceMatrix.needsUpdate = true;

        // Reset visual state
        this.setColor(idx, new THREE.Color(0xffffff));
        this.setEmissive(idx, new THREE.Color(0x000000));
        this.setUV(idx, 1, 1, 0);

        if (idx === this.highest) {
            while (this.highest >= 0 && !this.active.has(this.highest)) this.highest--;
            this.mesh.count = Math.max(0, this.highest + 1);
        }
    }

    setMatrix(idx: number, m: THREE.Matrix4) {
        this.ensure(idx);
        this.mesh.setMatrixAt(idx, m);
        this.mesh.instanceMatrix.needsUpdate = true;
    }

    setColor(idx: number, color: THREE.Color) {
        this.ensure(idx);
        const attr = this.mesh.geometry.getAttribute("instanceColor") as THREE.InstancedBufferAttribute;
        const o = idx * 3;
        (attr.array as Float32Array)[o + 0] = color.r;
        (attr.array as Float32Array)[o + 1] = color.g;
        (attr.array as Float32Array)[o + 2] = color.b;
        attr.needsUpdate = true;
    }

    setEmissive(idx: number, color: THREE.Color) {
        this.ensure(idx);
        const attr = this.mesh.geometry.getAttribute("instanceEmissive") as THREE.InstancedBufferAttribute;
        const o = idx * 3;
        (attr.array as Float32Array)[o + 0] = color.r;
        (attr.array as Float32Array)[o + 1] = color.g;
        (attr.array as Float32Array)[o + 2] = color.b;
        attr.needsUpdate = true;
    }

    setUV(idx: number, sx: number, sy: number, rot: number) {
        this.ensure(idx);
        const sAttr = this.mesh.geometry.getAttribute("instanceUVScale") as THREE.InstancedBufferAttribute;
        const rAttr = this.mesh.geometry.getAttribute("instanceUVRot") as THREE.InstancedBufferAttribute;
        const so = idx * 2;
        (sAttr.array as Float32Array)[so + 0] = sx;
        (sAttr.array as Float32Array)[so + 1] = sy;
        sAttr.needsUpdate = true;

        (rAttr.array as Float32Array)[idx] = rot;
        rAttr.needsUpdate = true;
    }

    setTexture(tex: THREE.Texture | null) {
        (this.mat.uniforms.uMap as any).value = tex;
        (this.mat.uniforms.uUseMap as any).value = tex ? 1 : 0;
        this.mat.needsUpdate = true;
    }
}


class ModuleInstancer {
    static _i: ModuleInstancer | null = null;
    static get I(): ModuleInstancer { return (this._i ??= new ModuleInstancer()); }

    private root: THREE.Object3D;
    private pools = new Map<PoolKey, ModuleInstancerPool>();
    private reg = new Map<ModuleInstancerPool, Map<number, InstancedModuleObject>>();

    private constructor() {
        const rootGuess = useViewerStore.getState().sceneViewer?.scene || null;
        this.root = rootGuess ?? new THREE.Group();
        if (!rootGuess) this.root.name = "InstancedModuleRoot";
    }

    private poolFor(key: PoolKey, tex?: THREE.Texture | null) {
        if (!this.pools.has(key)) {
            this.pools.set(key, new ModuleInstancerPool(key, tex ?? null, this.root));
        } else if (tex) {
            this.pools.get(key)!.setTexture(tex);
        }
        return this.pools.get(key)!;
    }

    acquire(key: PoolKey, tex: THREE.Texture | null, owner: InstancedModuleObject) {
        const p = this.poolFor(key, tex);
        const idx = p.alloc();
        if (!this.reg.has(p)) this.reg.set(p, new Map());
        this.reg.get(p)!.set(idx, owner);
        return { pool: p, index: idx };
    }

    release(pool: ModuleInstancerPool, idx: number) {
        pool.freeIndex(idx);
        this.reg.get(pool)?.delete(idx);
    }

    setMatrix(pool: ModuleInstancerPool, idx: number, m: THREE.Matrix4) {
        pool.setMatrix(idx, m);
    }
    setColor(pool: ModuleInstancerPool, idx: number, color: THREE.Color) {
        pool.setColor(idx, color);
    }
    setEmissive(pool: ModuleInstancerPool, idx: number, color: THREE.Color) {
        pool.setEmissive(idx, color);
    }
    setUV(pool: ModuleInstancerPool, idx: number, sx: number, sy: number, rot: number) {
        pool.setUV(idx, sx, sy, rot);
    }

    lookup(pool: ModuleInstancerPool, idx: number) {
        return this.reg.get(pool)?.get(idx);
    }

    getSharedGeometry(): THREE.BoxGeometry {
        const anyPool = this.pools.values().next().value as ModuleInstancerPool | undefined;
        return anyPool?.geom ?? new THREE.BoxGeometry(1, 1, 0.02);
    }
}


export class InstancedModuleObject extends THREE.Object3D {
    geometry: THREE.BoxGeometry;
    private _matFacade: MaterialFacade;

    private poolKey: PoolKey = "default";
    private pool: ModuleInstancerPool | null = null;
    private index = -1;

    private _visible = true;
    private _scale = new THREE.Vector3(1, 1, 1);
    private _map: THREE.Texture | null = null;
    private _lastColor = new THREE.Color(0xffffff);
    private _lastEmissive = new THREE.Color(0x000000);
    private _matrixNeedsUpdate = false;

    private _uvScale = new THREE.Vector2(1, 1);
    private _uvRot = 0;

    private static _rcMesh: THREE.Mesh | null = null;

    // extras your code uses:
    meshType?: string;
    module?: boolean;
    moduleId?: string;
    verInfo?: any;
    wPosArr: THREE.Vector3[] = [];
    edgeLineArr: Array<{ start: THREE.Vector3; end: THREE.Vector3 }> = [];
    roofId?: any;
    overPathIds?: any[];
    select?: boolean;
    first?: boolean;
    irrColor?: number;
    oriMat?: THREE.Material;

    constructor() {
        super();
        Object.defineProperty(this, "type", { value: "InstancedModuleObject", configurable: true });

        this.name = "module";
        this.matrixAutoUpdate = true;

        this.geometry = ModuleInstancer.I.getSharedGeometry();

        // Material facade
        this._matFacade = new MaterialFacade(this);

        Object.defineProperty(this, "material", {
            configurable: true,
            enumerable: true,
            get: () => this._matFacade,
            set: (m: any) => {
                if (m === this._matFacade) return;
                if (m && m.constructor && m.constructor.name === "MaterialFacade") return;

                // Restore path: material was a facade snapshot (from clone())
                if (m && m.__instancedSnapshot === true) {
                    this._matFacade.color.setHex(m.colorHex);
                    this._matFacade.emissive.setHex(m.emissiveHex);
                    this._matFacade.map = m.map ?? null;
                    this.__setInstanceUV(m.uvRepeat?.x ?? 1, m.uvRepeat?.y ?? 1, m.uvRotation ?? 0);
                    return;
                }

                if (m && typeof m === "object") {
                    // color
                    if (m.color !== undefined && m.color !== null) {
                        if (m.color.isColor) this._matFacade.color.setHex(m.color.getHex());
                        else if (typeof m.color === "number") this._matFacade.color.setHex(m.color);
                        else this._matFacade.color.set(m.color);
                    }
                    // emissive
                    if (m.emissive !== undefined && m.emissive !== null) {
                        if (m.emissive.isColor) this._matFacade.emissive.setHex(m.emissive.getHex());
                        else if (typeof m.emissive === "number") this._matFacade.emissive.setHex(m.emissive);
                        else this._matFacade.emissive.set(m.emissive);
                    }
                    if ("map" in m) {
                        const tex: THREE.Texture | null = m.map ?? null;
                        if (tex) {
                            this._matFacade.map = tex;
                        } else {
                            this._matFacade.map = null;
                        }
                    }
                }
            },
        });

        // visible
        Object.defineProperty(this, "visible", {
            configurable: true,
            enumerable: true,
            get: () => this._visible,
            set: (v: boolean) => {
                if (this._visible !== v) {
                    this._visible = v;
                    this._matrixNeedsUpdate = true;
                }
            },
        });

        // scale
        Object.defineProperty(this, "scale", {
            configurable: true,
            enumerable: true,
            get: () => this._scale,
            set: (v: THREE.Vector3) => {
                this._scale.copy(v);
                this._matrixNeedsUpdate = true;
            },
        });

        const originalUpdateMatrix = this.updateMatrix.bind(this);
        this.updateMatrix = () => { originalUpdateMatrix(); this._matrixNeedsUpdate = true; };

        const originalUpdateMatrixWorld = this.updateMatrixWorld.bind(this);
        this.updateMatrixWorld = (force?: boolean) => {
            originalUpdateMatrixWorld(force);
            if (this._matrixNeedsUpdate) {
                this.__pushMatrix();
                this._matrixNeedsUpdate = false;
            }
        };

        this.__ensureInstance();

        this.addEventListener("removed", () => this.__releaseInstance());
        this.addEventListener("added", () => {
            this.__ensureInstance();
            this._matrixNeedsUpdate = true;
        });
    }

    __setInstanceColor(col: THREE.Color | number | string) {
        this._lastColor = new THREE.Color(col as any);
        if (this.pool && this.index >= 0) {
            ModuleInstancer.I.setColor(this.pool, this.index, this._lastColor);
        }
    }

    __setInstanceEmissive(col: THREE.Color | number | string) {
        this._lastEmissive = new THREE.Color(col as any);
        if (this.pool && this.index >= 0) {
            ModuleInstancer.I.setEmissive(this.pool, this.index, this._lastEmissive);
        }
    }

    __setInstanceUV(sx: number, sy: number, rot: number) {
        this._uvScale.set(sx, sy);
        this._uvRot = rot;
        if (this.pool && this.index >= 0) {
            ModuleInstancer.I.setUV(this.pool, this.index, sx, sy, rot);
        }
    }

    __getUVScale() { return this._uvScale.clone(); }
    __getUVRot() { return this._uvRot; }

    __setPoolByMap(tex: THREE.Texture | null) {
        this._map = tex ?? null;
        const key = guessPoolKeyFromTexture(this._map);
        if (key === this.poolKey && this.pool) {
            this.pool.setTexture(this._map);
            return;
        }

        const prevPool = this.pool, prevIdx = this.index;
        if (prevPool && prevIdx >= 0) {
            ModuleInstancer.I.release(prevPool, prevIdx);
        }

        const got = ModuleInstancer.I.acquire(key, this._map, this);
        this.poolKey = key;
        this.pool = got.pool;
        this.index = got.index;

        this._matrixNeedsUpdate = true;
        this.__setInstanceColor(this._lastColor);
        this.__setInstanceEmissive(this._lastEmissive);
        this.__setInstanceUV(this._uvScale.x, this._uvScale.y, this._uvRot);
    }

    private __ensureInstance() {
        if (this.pool && this.index >= 0) return;
        const got = ModuleInstancer.I.acquire(this.poolKey, this._map, this);
        this.pool = got.pool;
        this.index = got.index;
        this._matrixNeedsUpdate = true;
        this.__setInstanceColor(this._lastColor);
        this.__setInstanceEmissive(this._lastEmissive);
        this.__setInstanceUV(this._uvScale.x, this._uvScale.y, this._uvRot);
    }

    private __releaseInstance() {
        if (this.pool && this.index >= 0) {
            ModuleInstancer.I.release(this.pool, this.index);
            this.pool = null;
            this.index = -1;
        }
    }

    private __pushMatrix() {
        if (!this.pool || this.index < 0) return;

        if (!this._visible || !this.parent) {
            const hide = new THREE.Matrix4().makeScale(0, 0, 0);
            ModuleInstancer.I.setMatrix(this.pool, this.index, hide);
            return;
        }

        this.updateWorldMatrix(true, false);
        ModuleInstancer.I.setMatrix(this.pool, this.index, this.matrixWorld);
    }

    override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
        if (!this._visible) return;

        if (!InstancedModuleObject._rcMesh) {
            InstancedModuleObject._rcMesh = new THREE.Mesh(this.geometry, new THREE.MeshBasicMaterial());
            InstancedModuleObject._rcMesh.geometry.computeBoundingBox();
            InstancedModuleObject._rcMesh.raycast = THREE.Mesh.prototype.raycast;
        }

        const m = InstancedModuleObject._rcMesh;
        m.matrixWorld.copy(this.matrixWorld);
        m.matrixAutoUpdate = false;

        const tmp: THREE.Intersection[] = [];
        THREE.Mesh.prototype.raycast.call(m, raycaster, tmp);
        for (const it of tmp) {
            intersects.push({ ...it, object: this });
        }
    }

    override getWorldPosition(target: THREE.Vector3): THREE.Vector3 {
        return super.getWorldPosition(target);
    }

    reset() {

        // Clean up existing instance
        this.dispose()



        // Reset all internal state
        this._visible = true;
        this._scale.set(1, 1, 1);
        this._map = null;
        this._lastColor.set(0xffffff);
        this._lastEmissive.set(0x000000);
        this._matrixNeedsUpdate = false;
        this._uvScale.set(1, 1);
        this._uvRot = 0;

        this.poolKey = "default";
        this.pool = null;
        this.index = -1;

        // Reset extra fields your code uses
        this.meshType = undefined;
        this.module = undefined;
        this.moduleId = undefined;
        this.verInfo = undefined;
        this.wPosArr = [];
        this.edgeLineArr = [];
        this.roofId = undefined;
        this.overPathIds = [];
        this.select = undefined;
        this.first = undefined;
        this.irrColor = undefined;
        this.oriMat = undefined;

        // Ensure fresh instancing next frame
        this.__ensureInstance();
        this._matrixNeedsUpdate = true;

    }


    dispose() {
        this.__releaseInstance();
        // Remove from parent (scene or group) if present
        if (this.parent) {
            this.parent.remove(this);
        }

        // Dispose geometry & material if they exist
        if (this.geometry) {
            this.geometry.dispose();
        }

        this.removeEventListener("removed", () => this.__releaseInstance());
        this.removeEventListener("added", () => {
            this.__ensureInstance();
            this._matrixNeedsUpdate = true;
        });
    }
}
